import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker } from "react-day-picker";

import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({ className, classNames, showOutsideDays = false, ...props }: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-2 sm:p-3", className)}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0 justify-center",
        month: "space-y-3 sm:space-y-4",
        caption: "flex justify-center pt-1 relative items-center",
        caption_label: "text-sm sm:text-base font-medium",
        nav: "space-x-1 flex items-center",
        nav_button: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 sm:h-8 sm:w-8 bg-transparent p-0 opacity-50 hover:opacity-100",
        ),
        nav_button_previous: "absolute left-0 sm:left-1",
        nav_button_next: "absolute right-0 sm:right-1",
        table: "w-full border-collapse space-y-1",
        head_row: "flex justify-center",
        head_cell: "text-muted-foreground rounded-md w-8 sm:w-9 font-normal text-[0.7rem] sm:text-[0.8rem]",
        row: "flex w-full mt-2 justify-center",
        cell: "h-8 w-8 sm:h-9 sm:w-9 text-center text-xs sm:text-sm p-0 relative focus-within:relative focus-within:z-20",
        day: cn(buttonVariants({ variant: "ghost" }), "h-8 w-8 sm:h-9 sm:w-9 p-0 font-normal aria-selected:opacity-100 hover:bg-gold/10 hover:text-gold transition-all text-xs sm:text-sm"),
        day_range_end: "day-range-end rounded-r-full bg-gold text-charcoal font-bold border-2 border-gold shadow-[0_0_15px_rgba(212,175,55,0.5)] hover:shadow-[0_0_20px_rgba(212,175,55,0.7)] relative after:content-[''] after:absolute after:right-0 after:top-1/2 after:-translate-y-1/2 after:w-1 after:h-6 after:bg-gold-glow after:rounded-l",
        day_selected:
          "bg-gold text-charcoal hover:bg-gold-glow hover:text-charcoal font-bold border-2 border-gold shadow-[0_0_15px_rgba(212,175,55,0.5)]",
        day_today: "bg-charcoal/50 text-ivory border-2 border-gold/60 font-medium",
        day_outside:
          "day-outside text-muted-foreground opacity-30 !bg-transparent hover:!bg-transparent aria-selected:!bg-transparent aria-selected:!text-muted-foreground before:!hidden after:!hidden !border-0 !shadow-none",
        day_disabled: "text-muted-foreground opacity-50",
        day_range_middle: "aria-selected:bg-gradient-to-r aria-selected:from-gold/15 aria-selected:via-gold/10 aria-selected:to-gold/15 aria-selected:text-ivory/90 hover:aria-selected:bg-gold/20",
        day_range_start: "rounded-l-full bg-gold text-charcoal font-bold border-2 border-gold shadow-[0_0_15px_rgba(212,175,55,0.5)] hover:shadow-[0_0_20px_rgba(212,175,55,0.7)] relative before:content-[''] before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:w-1 before:h-6 before:bg-gold-glow before:rounded-r",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        IconLeft: ({ ..._props }) => <ChevronLeft className="h-4 w-4" />,
        IconRight: ({ ..._props }) => <ChevronRight className="h-4 w-4" />,
      }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
